import { Stack, TooltipProps, Typography } from '@mui/material';
import { useFlag } from '@schooly/hooks/use-flag';
import { ChevronDownSmallIcon } from '@schooly/style';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useIntl } from 'react-intl';

import { DropdownSelectRow } from '../DropdownSelectRow';
import { ExpandedSelect } from '../ExpandedSelect';
import { FiltersDropdownButton } from './FiltersDropdownButton';
import { FiltersDropdownContainer } from './FiltersDropdownContainer';

declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export type FilterDropdownSingleSelectProps<T> = {
  label?: string;
  options: readonly T[];
  selectedOption?: T;
  onSelectOption: (v: T) => void;
  onClear?: () => void;
  onRemove?: () => void;
  getOptionLabelId: (o: T) => string;
  disabled?: boolean;
  renderLabel?: () => React.ReactNode;
} & Partial<TooltipProps>;

export type FilterDropdownSingleSelect = {
  open: () => void;
};

const FilterDropdownSingleSelectComponent = <T extends string | number>(
  {
    label,
    options,
    selectedOption,
    onSelectOption,
    onClear,
    onRemove,
    getOptionLabelId,
    disabled,
    renderLabel,
    ...tooltipProps
  }: FilterDropdownSingleSelectProps<T>,
  ref: React.ForwardedRef<FilterDropdownSingleSelect>,
) => {
  const { $t } = useIntl();
  const [opened, open, close] = useFlag();

  const handleRemove = useCallback(() => {
    close();
    onRemove?.();
  }, [close, onRemove]);

  useImperativeHandle(
    ref,
    () => ({
      open,
    }),
    [open],
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    open();
  }, [disabled, open]);

  return (
    <FiltersDropdownContainer
      opened={opened}
      onOpen={handleOpen}
      onClose={close}
      disabled={disabled}
      width={250}
      title={
        <ExpandedSelect
          width={250}
          hasSelectedValue={selectedOption !== undefined}
          renderContent={() => (
            <Stack gap={0.5} m={1}>
              {options.map((option) => (
                <DropdownSelectRow
                  key={option}
                  isSelected={selectedOption === option}
                  onClick={() => {
                    onSelectOption(option);
                    close();
                  }}
                >
                  {$t({
                    id: getOptionLabelId(option),
                  })}
                </DropdownSelectRow>
              ))}
            </Stack>
          )}
          onClose={close}
          onClear={onClear}
        >
          {selectedOption !== undefined && (
            <Typography variant="h3">
              {$t({
                id: getOptionLabelId(selectedOption),
              })}
            </Typography>
          )}
        </ExpandedSelect>
      }
      {...tooltipProps}
      renderClosed={() => (
        <Stack
          flexDirection="row"
          gap={0.5}
          onClick={handleOpen}
          sx={{ cursor: disabled ? 'default' : 'pointer' }}
          alignItems="center"
        >
          {renderLabel?.() ?? (
            <>
              <Typography variant="h3" color="common.grey3">
                {label || $t({ id: 'filter-ArrangeBy' })}
                {selectedOption ? ':' : ''}
              </Typography>
              {selectedOption !== undefined && (
                <Typography variant="h3" onClick={handleOpen}>
                  {$t({
                    id: getOptionLabelId(selectedOption),
                  })}
                </Typography>
              )}
            </>
          )}
          <ChevronDownSmallIcon color="black" />
        </Stack>
      )}
      renderOpened={() => (
        <FiltersDropdownButton
          onClick={(e) => {
            e.stopPropagation();
            close();
          }}
          onClear={onRemove ? handleRemove : undefined}
        >
          {label || $t({ id: 'filter-ArrangeBy' })}
        </FiltersDropdownButton>
      )}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const FilterDropdownSingleSelect = forwardRef(FilterDropdownSingleSelectComponent);
